/**
 * Client-side result filtering utilities.
 * Supports substring matching, numeric comparisons (>, <, >=, <=, range),
 * null matching, negation (!prefix), and global (cross-column) search.
 */

function toStringValue(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  return String(value);
}

function tryNumeric(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return isNaN(n) ? null : n;
  }
  return null;
}

const COMPARISON_RE = /^(>=|<=|>|<)(-?\d+\.?\d*)$/;
const RANGE_RE = /^(-?\d+\.?\d*)\.\.(-?\d+\.?\d*)$/;

function matchesNumericFilter(value: unknown, filter: string): boolean | null {
  const compMatch = COMPARISON_RE.exec(filter);
  if (compMatch) {
    const num = tryNumeric(value);
    if (num === null) return false;
    const threshold = Number(compMatch[2]);
    switch (compMatch[1]) {
      case '>': return num > threshold;
      case '<': return num < threshold;
      case '>=': return num >= threshold;
      case '<=': return num <= threshold;
    }
  }

  const rangeMatch = RANGE_RE.exec(filter);
  if (rangeMatch) {
    const num = tryNumeric(value);
    if (num === null) return false;
    const lo = Number(rangeMatch[1]);
    const hi = Number(rangeMatch[2]);
    return num >= lo && num <= hi;
  }

  return null; // not a numeric filter
}

/**
 * Match a single cell value against a filter string.
 *
 * Design choice: Option A — substring match on String(value) for all types.
 * All values (number, bigint, boolean, string) are coerced to string via
 * `toStringValue()` before substring matching. This is consistent, predictable,
 * and matches user expectation ("type 5, see rows containing 5").
 * Numeric comparison operators (>, <, >=, <=, range) are opt-in via prefix syntax.
 *
 * Filter syntax:
 *   - Plain text → case-insensitive substring match (works for all types)
 *   - `>N`, `<N`, `>=N`, `<=N` → numeric comparison
 *   - `A..B` → numeric range (inclusive)
 *   - `!text` → negation (exclude matching rows)
 *   - `null` → matches null/undefined values
 */
export function matchesFilter(value: unknown, filter: string): boolean {
  if (!filter || filter.trim() === '') return true;

  const trimmed = filter.trim();

  // Negation
  if (trimmed.startsWith('!') && trimmed.length > 1) {
    return !matchesFilter(value, trimmed.slice(1));
  }

  // Null matching
  if (value === null || value === undefined) {
    return trimmed.toLowerCase() === 'null';
  }

  // Try numeric filter operators
  const numResult = matchesNumericFilter(value, trimmed);
  if (numResult !== null) return numResult;

  // Default: case-insensitive substring match on string representation
  const strValue = toStringValue(value).toLowerCase();
  return strValue.includes(trimmed.toLowerCase());
}

/**
 * Match a row against a global search term (OR across all columns).
 * Returns true if any column value in the row matches the search term.
 */
export function matchesGlobalFilter(
  row: Record<string, unknown>,
  globalFilter: string,
  columnKeys: string[],
): boolean {
  if (!globalFilter || globalFilter.trim() === '') return true;

  const term = globalFilter.trim().toLowerCase();
  return columnKeys.some((key) => {
    const value = row[key];
    if (value === null || value === undefined) {
      return term === 'null';
    }
    return toStringValue(value).toLowerCase().includes(term);
  });
}

/**
 * Apply column filters + global filter to a result set.
 * Column filters use AND logic; global filter uses OR across columns.
 * Both must pass for a row to be included.
 */
export function applyResultFilters(
  rows: Record<string, unknown>[],
  columnFilters: Record<string, string>,
  globalFilter: string,
  columnKeys: string[],
): Record<string, unknown>[] {
  const hasColumnFilters = Object.values(columnFilters).some((f) => f.trim() !== '');
  const hasGlobalFilter = globalFilter.trim() !== '';

  if (!hasColumnFilters && !hasGlobalFilter) return rows;

  return rows.filter((row) => {
    // Column filters: ALL must match (AND)
    if (hasColumnFilters) {
      for (const [colId, filter] of Object.entries(columnFilters)) {
        if (filter.trim() === '') continue;
        if (!matchesFilter(row[colId], filter)) return false;
      }
    }
    // Global filter: ANY column must match (OR)
    if (hasGlobalFilter) {
      if (!matchesGlobalFilter(row, globalFilter, columnKeys)) return false;
    }
    return true;
  });
}
